/**
 * `.rl-outcome` — one Reel Option on the selected reel, and everything an
 * operator can do to it (CAE-1552).
 *
 * Not shared with the Contest Overview's `reel-outcome-table.tsx`. CAE-1574 read
 * the two side by side and settled it: that one is read-only, gives the decimal
 * and the fraction a column each and adds a settlement column; this one stacks
 * the two prices in one 90px column and adds drag, artwork, a toggle and four
 * actions. What they genuinely share is the status indicator, which is why
 * `OutcomeStatus` is in the kit and the rest of the row is not.
 */
import type { KeyboardEvent } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import type { Theme } from '@mui/material/styles';
import {
  effectiveClubAbbreviation,
  effectiveJerseyNumber,
  resolveFixtureLabel,
  resolvePickName,
} from '@s2w/core';
import type { Asset } from '../../../../api/assets';
import type { ContestDetailReelOption, ContestFixture, ReelKind } from '../../../../api/contests';
import { reelOptionMarketLabel } from '../../../../contests/reel-option-market-label';
import { needsArtwork } from '../../../../contests/reel-stats';
import { OutcomeStatus, Toggle, TrashIcon } from '../../../../components/ui';
import { toSafePreviewUrl } from '../../../../utils/safe-url';
import { REEL_OPTION_COLUMNS } from './reel-option-layout';
import { ReelOptionInfo } from './reel-option-info';
import { ReelOptionMenu } from './reel-option-menu';

/** `.rl-asset` — the artwork square, which is also the button that sets it. */
const ASSET_PX = 56;

/**
 * The narrowest the row's fixture and market labels are allowed to get before
 * the table scrolls instead. Enough to read a clipped field by — "Both To
 * Qual…" — where free shrinking took them down to one character each.
 * `REEL_OPTION_TABLE_MIN_WIDTH` is sized to keep both floors reachable.
 */
const META_FIELD_MIN_PX = 76;

const DragGripIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
    {[6, 12, 18].map((cy) => (
      <g key={cy}>
        <circle cx="9" cy={cy} r="1.4" fill="currentColor" />
        <circle cx="15" cy={cy} r="1.4" fill="currentColor" />
      </g>
    ))}
  </svg>
);

/** `.m-dot-sep` — the 3px bullet between the row's two meta fields. */
const MetaDot = () => (
  <Box
    aria-hidden
    component="span"
    sx={(theme: Theme) => ({
      width: '3px',
      height: '3px',
      flexShrink: 0,
      borderRadius: '50%',
      backgroundColor: theme.tokens.componentColor.dotSeparator,
    })}
  />
);

const ImageSlotIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M7 15l3-3 4 4 3-3 4 4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="9" cy="10" r="1.3" fill="currentColor" />
  </svg>
);

export interface ReelOptionRowProps {
  option: ContestDetailReelOption;
  /** Its place on the reel, and what a keyboard reorder moves it from. */
  index: number;
  rowCount: number;
  fixture: ContestFixture | undefined;
  asset: Asset | undefined;
  /** Every write is gated on the contest still being inside its edit window. */
  editable: boolean;
  busy: boolean;
  onToggleEnabled: (enabled: boolean) => void;
  onMoveTo: (position: number) => void;
  onPickAsset: () => void;
  onRemove: () => void;
  onEditDisplayName: () => void;
  /** Opens the club and jersey number override (CAE-1610). */
  onEditParticipant: () => void;
  onClearAsset: () => void;
  /**
   * The Reel's kind, which decides which of the club's images stands in for
   * a missing asset: the jersey on a Player Market Reel, the logo on a Game
   * Market Reel (GL-12). Null while the Reel has no kind yet.
   */
  reelKind: ReelKind | null;
  /** Picking the row up — on the grip, so only the grip starts a drag. */
  gripDragProps: {
    draggable: boolean;
    onDragStart: (event: React.DragEvent<HTMLElement>) => void;
    onDragEnd: () => void;
  };
  /** Putting another row down on this one. The list owns the reorder itself. */
  rowDropProps: {
    onDragOver: (event: React.DragEvent<HTMLElement>) => void;
    onDragLeave: (event: React.DragEvent<HTMLElement>) => void;
    onDrop: (event: React.DragEvent<HTMLElement>) => void;
  };
  isDragging: boolean;
  isDropTarget: boolean;
}

export const ReelOptionRow = ({
  option,
  index,
  rowCount,
  fixture,
  asset,
  editable,
  busy,
  onToggleEnabled,
  onMoveTo,
  onPickAsset,
  onRemove,
  onEditDisplayName,
  onEditParticipant,
  onClearAsset,
  reelKind,
  gripDragProps,
  rowDropProps,
  isDragging,
  isDropTarget,
}: ReelOptionRowProps) => {
  /*
   * `resolvePickName`, not `displayNameOverride || outcomeName`. CONTEXT.md puts
   * the rule in `@s2w/core` precisely because the override column is
   * unconstrained — a historical row holding `'   '` passes `||` and puts a
   * blank label on a row an operator then cannot identify.
   */
  const pickName = resolvePickName(option);
  const missingAsset = needsArtwork(option, reelKind);
  /*
   * An asset attached by hand wins; otherwise the option's club lends its
   * image, by the Reel's kind (GL-12; CAE-1610). The same rule publish holds
   * it to, so a row drawn with a thumbnail is a row publish will not refuse.
   */
  const clubImageUrl =
    reelKind === 'player'
      ? option.clubJerseyUrl
      : reelKind === 'game'
        ? option.clubLogoUrl
        : (option.clubJerseyUrl ?? option.clubLogoUrl);
  const previewUrl = option.assetId
    ? toSafePreviewUrl(asset?.previewUrl)
    : toSafePreviewUrl(clubImageUrl ?? undefined);
  const clubAbbreviation = effectiveClubAbbreviation(option);
  const jerseyNumber = effectiveJerseyNumber(option);
  const participantOverridden =
    option.clubAbbreviationOverride !== null || option.jerseyNumberOverride !== null;
  const participantLabel = [clubAbbreviation, jerseyNumber === null ? null : `#${jerseyNumber}`]
    .filter((part): part is string => part !== null)
    .join(' · ');

  /**
   * The keyboard half of reordering (CAE-1552's fourth acceptance criterion).
   *
   * A grip you can only drag is a control a keyboard cannot reach at all, and
   * reordering is not a convenience here — the order is the order a player sees
   * the options in. Arrow keys on the focused grip move the row by one and the
   * focus follows it, so a run of presses walks an option up the list the way
   * holding it and dragging would.
   *
   * **The grip is never `disabled` mid-flight**, which is why this guards on
   * `busy` itself rather than letting the attribute do it.
   *
   * A browser blurs an element the moment it becomes disabled and hands focus to
   * `<body>`; re-enabling does not hand it back. With `disabled={busy}` every
   * arrow press therefore cost a keyboard user their place and a walk back
   * through the page's tab order — the whole capability this exists to provide,
   * lost on its first use. Found by pressing the key on the real screen and
   * reading `document.activeElement`, and **not reproducible under test**: jsdom
   * does not implement that blur, so the keyboard test below passes either way.
   * The guard is this comment and the live check, not a red test.
   *
   * Dragging is still gated, on `gripDragProps.draggable` — a drag begun
   * mid-reorder would compute its target against a list about to change — and
   * `draggable` is an attribute that does not touch focus.
   */
  const onGripKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
    if (step === 0 || !editable || busy) return;

    const next = index + step;
    // Clamped, not wrapped: the ends of a reel are real, and an option that
    // jumped from last to first on one keypress would look like a bug.
    if (next < 0 || next >= rowCount) return;

    event.preventDefault();
    onMoveTo(next);
  };

  return (
    <Box
      role="row"
      data-testid={`reel-option-${option.id}-row`}
      {...rowDropProps}
      sx={(theme: Theme) => ({
        display: 'grid',
        gridTemplateColumns: REEL_OPTION_COLUMNS,
        gap: '14px',
        alignItems: 'center',
        padding: '10px 14px',
        borderTop: theme.tokens.border.default,
        transition: 'background 0.12s',
        '&:first-of-type': { borderTop: 0 },
        '&:hover': { backgroundColor: theme.tokens.color.surfaceMuted },
        /*
         * Dimming is for *disabled*, and only disabled — the same rule the
         * Overview's table follows. A suspended option is a different fact
         * about a different actor, and the Status column is where it is said.
         */
        ...(option.enabled ? {} : { opacity: 0.55 }),
        ...(isDragging ? { opacity: 0.55 } : {}),
        /*
         * `.rl-outcome.has-issue` — a warn edge on a row whose option is live
         * but has nothing to show. The class is named in the screen README and
         * has no rule in the stylesheet, so this follows the prose.
         */
        ...(missingAsset ? { boxShadow: `inset 3px 0 0 0 ${theme.tokens.color.warn}` } : {}),
        ...(isDropTarget
          ? { outline: `2px dashed ${theme.tokens.color.accent}`, outlineOffset: '-2px' }
          : {}),
      })}
    >
      <Box role="cell" sx={{ display: 'flex', justifyContent: 'center' }}>
        <ButtonBase
          disabled={!editable}
          aria-label={`Reorder ${pickName} — position ${index + 1} of ${rowCount}. Use the arrow keys to move it.`}
          onKeyDown={onGripKeyDown}
          {...gripDragProps}
          sx={(theme: Theme) => ({
            width: '24px',
            height: '32px',
            borderRadius: `${theme.tokens.radius.badge}px`,
            display: 'grid',
            placeItems: 'center',
            color: theme.tokens.componentColor.dotSeparator,
            cursor: editable && !busy ? 'grab' : 'not-allowed',
            transition: 'color 0.12s, background 0.12s',
            '&:hover': {
              color: theme.tokens.color.ink3,
              backgroundColor: theme.tokens.componentColor.countBadgeSelectedBg,
            },
            '&:active': { cursor: editable && !busy ? 'grabbing' : 'not-allowed' },
          })}
        >
          <DragGripIcon />
        </ButtonBase>
      </Box>

      <Box role="cell" sx={{ display: 'flex', justifyContent: 'center' }}>
        <ButtonBase
          onClick={onPickAsset}
          disabled={!editable}
          aria-label={previewUrl ? `Change image for ${pickName}` : `Add image for ${pickName}`}
          sx={(theme: Theme) => ({
            width: `${ASSET_PX}px`,
            height: `${ASSET_PX}px`,
            borderRadius: `${theme.tokens.radius.insetRow}px`,
            display: 'grid',
            placeItems: 'center',
            overflow: 'hidden',
            backgroundColor: theme.tokens.color.surface,
            border: previewUrl
              ? `${theme.tokens.border.width} solid ${theme.tokens.color.line}`
              : `${theme.tokens.border.width} dashed ${
                  missingAsset
                    ? theme.tokens.componentColor.marketBadgeEmptyBorder
                    : theme.tokens.color.lineStrong
                }`,
            color: missingAsset ? theme.tokens.color.warn : theme.tokens.color.ink4,
            transition: 'border-color 0.12s, background 0.12s',
            '&:hover': {
              borderColor: theme.tokens.color.accent,
              color: theme.tokens.color.accent,
            },
          })}
        >
          {previewUrl ? (
            <Box
              component="img"
              src={previewUrl}
              alt=""
              /*
               * Sized in pixels rather than `100%`: the square centres its
               * content, and a centred grid item sizes to that content — so a
               * percentage height resolves back to `auto` and `cover` never
               * gets a square to crop into. Same trap CAE-1574 documented on
               * the Overview's 24px thumb.
               */
              sx={{
                width: `${ASSET_PX}px`,
                height: `${ASSET_PX}px`,
                objectFit: 'cover',
                display: 'block',
              }}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '2px',
                fontSize: '10px',
                fontWeight: 600,
                letterSpacing: '0.02em',
              }}
            >
              <ImageSlotIcon />
              <span>Add</span>
            </Box>
          )}
        </ButtonBase>
      </Box>

      <Box role="cell" sx={{ minWidth: 0 }}>
        <Box
          sx={(theme: Theme) => ({
            fontSize: '14.5px',
            fontWeight: 600,
            letterSpacing: '-0.005em',
            color: theme.tokens.color.ink,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          })}
        >
          {pickName}
        </Box>
        {/*
         * One line, clipped — never wrapped.
         *
         * The handoff's mock reads "Mexico vs South Africa · 90 Minutes" and
         * fits; the feed's real names do not. "FIFA World Cup 2026 - Group A ·
         * Both To Qualify From Group · Missing asset" wrapped to three lines in
         * a 202px column, taking the row from the specified ~58px to 113 and
         * leaving a separator dot dangling at the end of each line. The
         * Overview's table clips the same two fields for the same reason
         * (CAE-1574); matching it keeps the two screens' rows the same height
         * as well as the same shape.
         *
         * The warning is the part that must not be clipped, so it is the part
         * that does not shrink: a row whose problem scrolled off the end of its
         * own subtitle is a row that looks fine and is not.
         */}
        <Box
          sx={(theme: Theme) => ({
            marginTop: '3px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '12.5px',
            color: theme.tokens.color.ink3,
            whiteSpace: 'nowrap',
          })}
        >
          {/*
           * Both fields stop shrinking at `META_FIELD_MIN_PX`, and the market
           * gives way first on the way down.
           *
           * Left to shrink freely they starve together and neither identifies
           * the row — the real data rendered "FIFA W… · Both …" at the design's
           * own width. A floor means a clipped field still carries enough to
           * read ("Both To Qual…"), and when even the floors do not fit the
           * table scrolls inside itself rather than degrading to initials. That
           * is the trade CAE-1542 and CAE-1574 both made for their wide tables:
           * scroll, never silently drop. Each carries a `title`, so the full
           * text is a hover away regardless.
           */}
          <Box
            component="span"
            title={fixture ? resolveFixtureLabel(fixture) : undefined}
            sx={(theme: Theme) => ({
              color: theme.tokens.color.ink2,
              fontWeight: 500,
              flexShrink: 1,
              minWidth: `${META_FIELD_MIN_PX}px`,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            })}
          >
            {fixture ? resolveFixtureLabel(fixture) : 'Fixture not found'}
          </Box>
          <MetaDot />
          <Box
            component="span"
            title={reelOptionMarketLabel(option)}
            sx={{
              flexShrink: 3,
              minWidth: `${META_FIELD_MIN_PX}px`,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {reelOptionMarketLabel(option)}
          </Box>
          {participantLabel !== '' ? (
            <>
              <MetaDot />
              <Box
                component="span"
                title={
                  participantOverridden
                    ? 'Club or number set by an operator'
                    : 'Club and number as the feed placed the pick'
                }
                data-testid={`reel-option-${option.id}-participant`}
                sx={(theme: Theme) => ({
                  flexShrink: 0,
                  color: theme.tokens.color.ink2,
                  fontWeight: 500,
                })}
              >
                {participantLabel}
                {participantOverridden ? ' (set)' : ''}
              </Box>
            </>
          ) : null}
          {missingAsset ? (
            <>
              <MetaDot />
              <Box
                component="span"
                sx={(theme: Theme) => ({
                  flexShrink: 0,
                  color: theme.tokens.color.warn,
                  fontWeight: 500,
                })}
              >
                Missing asset
              </Box>
            </>
          ) : null}
        </Box>
      </Box>

      <Box
        role="cell"
        sx={(theme: Theme) => ({
          textAlign: 'right',
          fontFamily: theme.tokens.font.mono,
          fontVariantNumeric: 'tabular-nums',
        })}
      >
        {/*
         * An em-dash rather than a zero when the catalogue no longer carries the
         * outcome — a price nobody quoted is not a price of nothing.
         */}
        <Box
          sx={(theme: Theme) => ({
            fontSize: '14px',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: theme.tokens.color.ink,
          })}
        >
          {option.oddsDecimal === null ? '—' : option.oddsDecimal.toFixed(2)}
        </Box>
        <Box
          sx={(theme: Theme) => ({
            marginTop: '2px',
            fontSize: '11.5px',
            color: theme.tokens.color.ink3,
          })}
        >
          {/* Verbatim: the book priced this fraction, and it is not the decimal rounded. */}
          {option.oddsFractional ?? '—'}
        </Box>
      </Box>

      <Box role="cell" sx={{ display: 'flex', alignItems: 'center' }}>
        <OutcomeStatus suspended={option.suspended} />
      </Box>

      <Box role="cell" sx={{ display: 'flex', justifyContent: 'center' }}>
        {/*
         * Gated on `editable` only, not on `busy` — the same reason the grip is.
         * `busy` is true while *any* row's write is in flight, so disabling on
         * it would blur a switch an operator had just pressed Space on, and
         * strand focus on `<body>`. A second press before the refetch lands
         * re-sends the state the switch is already showing, which the endpoint
         * takes idempotently; losing a keyboard user's place is the worse of the
         * two.
         */}
        <Toggle
          checked={option.enabled}
          onChange={onToggleEnabled}
          label={`${pickName} enabled`}
          hideLabel
          disabled={!editable}
        />
      </Box>

      <Box
        role="cell"
        sx={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}
      >
        <ReelOptionInfo option={option} pickName={pickName} fixture={fixture} />
        <Tooltip title="Remove from reel">
          <span>
            <IconButton
              size="small"
              disabled={!editable}
              onClick={onRemove}
              aria-label={`Remove ${pickName} from the reel`}
              sx={(theme: Theme) => ({
                /*
                 * `.rl-iconbtn`'s 28x28, the same box the info trigger beside
                 * it keeps. Without it MUI sizes `size="small"` off the glyph,
                 * so the 14px below would drop the hit target to 24px -- the
                 * floor exactly, with nothing in hand (CAE-1608).
                 */
                width: '28px',
                height: '28px',
                color: theme.tokens.color.ink4,
                '&:hover': {
                  color: theme.tokens.color.danger,
                  backgroundColor: theme.tokens.color.dangerSoft,
                },
              })}
            >
              {/*
               * 14x14, the size the handoff draws `MIcon.trash` at. Bare, it
               * took MUI's 1.5rem and drew at 24 -- which put the actions cell
               * 12px over the 88px track `REEL_OPTION_COLUMNS` gives it, the
               * overflow landing in the outcome name's column. With this and
               * the info trigger's 12px glyph the three controls measure
               * 28 + 28 + 24 and their two 4px gaps: 88 exactly (CAE-1608).
               */}
              <TrashIcon sx={{ fontSize: '14px' }} />
            </IconButton>
          </span>
        </Tooltip>
        <ReelOptionMenu
          pickName={pickName}
          hasAsset={option.assetId !== null}
          disabled={!editable}
          onEditDisplayName={onEditDisplayName}
          onEditParticipant={onEditParticipant}
          onReplaceAsset={onPickAsset}
          onClearAsset={onClearAsset}
          onRemove={onRemove}
        />
      </Box>
    </Box>
  );
};
